import { FlexRow } from "@design-system/Layout/Flex";
import {
    ObjectiveStatus,
    ResponseStatus,
    type GetObjectiveQuery,
    type UpdateObjectiveMutation,
} from "@generated/client/graphql";
import { useObjectiveFooterTranslations } from "./i18n";

import { useSetToast } from "@app/components/Toast";
import { useUpdateObjective } from "@app/screens/Objectives/data";
import { currentUserRoleAtom } from "@app/store/userStore";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { useAtomValue } from "jotai";
import { useMemo, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { canValidateObjective } from "../../services";
type ObjectiveFooterProps = {
    objective: GetObjectiveQuery["objective"];
};

export const ObjectiveFooter: FC<ObjectiveFooterProps> = ({ objective }) => {
    const { t, keys } = useObjectiveFooterTranslations();
    const { setToastError } = useSetToast();
    const navigate = useNavigate();

    const requiredFieldsAreMissing = useMemo(
        () => canValidateObjective(objective),
        [objective],
    );

    const isContributor = useAtomValue(currentUserRoleAtom).isContributor;

    const updateEntityMutationOpts = {
        onSettled: (data: UpdateObjectiveMutation | undefined) => {
            if (data?.updateObjective?.status === ResponseStatus.Error) {
                setToastError(`${data.updateObjective.error?.message}`);
            }
        },
        onError: () => {
            setToastError(t(keys.error_update));
        },
    };

    const updateObjectiveMutation = useUpdateObjective(
        objective.id,
        objective.entityId,
    );

    const handleValidate = () => {
        updateObjectiveMutation.mutate(
            { id: objective.id, status: ObjectiveStatus.Defined },
            updateEntityMutationOpts,
        );
    };

    const handleEdit = () => {
        updateObjectiveMutation.mutate(
            { id: objective.id, status: ObjectiveStatus.Draft },
            updateEntityMutationOpts,
        );
    };

    const handleBack = () => {
        navigate("..");
    };

    return (
        <FlexRow
            as="footer"
            className="border-t border-tertiary"
            alignItems="center"
            p="4"
            gap="4"
        >
            {objective.status === ObjectiveStatus.Defined ? (
                <Button
                    className="flex-1"
                    variant="outlined"
                    size="sm"
                    onClick={handleEdit}
                >
                    <Button.Icon name="edit" />
                    {t(keys.edit)}
                </Button>
            ) : (
                <>
                    <Button
                        className="flex-1"
                        variant="outlined"
                        size="sm"
                        onClick={handleBack}
                    >
                        {t(keys.save_and_close)}
                    </Button>
                    {!isContributor && (
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Box className="flex-1">
                                    <Button
                                        className="w-full"
                                        variant="flatPrimary"
                                        size="sm"
                                        disabled={
                                            !requiredFieldsAreMissing ||
                                            isContributor
                                        }
                                        onClick={handleValidate}
                                    >
                                        {t(keys.validate)}
                                    </Button>
                                </Box>
                            </Tooltip.Trigger>
                            {!requiredFieldsAreMissing && (
                                <Tooltip.Content>
                                    {t(
                                        keys.fill_missing_required_inputs_tooltip,
                                    )}
                                </Tooltip.Content>
                            )}
                        </Tooltip>
                    )}
                </>
            )}
        </FlexRow>
    );
};
